import { Show, createMemo, type JSX } from 'solid-js'
import type { HistoryRowScope } from 'mormaii-web-presentation/patient-detail'

import Css from './history-cells.module.scss'
import { Icon } from '../icons'

/**
 * A celula de uma coluna do historico, pelo CAMPO dela.
 *
 * A tabela pede uma celula por campo e a identifica por ele: a primeira coluna e
 * sempre a data ou a hora, e as outras vem de `row.cells`, achadas pelo campo.
 *
 * A primeira coluna e clicavel so quando a linha E um dia - nas leituras de um dia
 * nao ha nada mais fundo para abrir, e `day` vazio e o que diz isso.
 */
export function historyCell(row: HistoryRowScope, field: string): JSX.Element {
    if (field === 'date') {
        return (
            <Show when={row.day} fallback={<span class={Css.number}>{row.label}</span>}>
                <button type="button" onClick={() => void row.onOpen()} class={Css.day}>
                    {row.label}
                </button>
            </Show>
        )
    }

    return <MeasurementOf row={row} field={field} />
}

/**
 * A celula de uma medicao, achada pelo campo NUM MEMO.
 *
 * A linha reaproveitada recebe a cada recarga (ordenar, paginar) um array NOVO de
 * celulas - com as mesmas celulas dentro. Achada fora do memo, a troca do array refaria
 * o DOM de toda celula de medicao; no memo, a mesma celula volta e o DOM dela fica.
 */
function MeasurementOf(props: { row: HistoryRowScope; field: string }): JSX.Element {
    const cell = createMemo(() => props.row.cells.find((item) => item.field === props.field))
    return (
        <Show when={cell()} fallback={<span class={Css.empty}>-</span>}>
            {(scope) => measurementCell(scope())}
        </Show>
    )
}

function measurementCell(cell: HistoryRowScope['cells'][number]): JSX.Element {
    return (
        /*
            A celula deixou de ser uma leitura: agrupada por hora, ela resume N delas.

            Tres coisas dizem o que o resumo esconde, e cada uma ganhou a sua linha de
            razao:

            - a AMPLITUDE embaixo do numero, quando as pontas divergem. E o que a media
              esconde, mostrado em vez de sinalizado em abstrato - quem le e
              profissional de saude e interpreta, e nao precisamos inventar limiar
              clinico nenhum.
            - a marca de LEITURA UNICA, que nao diz que a medicao e anormal: diz que
              este numero nao e uma media. `92` de uma leitura e `92` de quarenta se
              pareciam.
            - o CLIQUE, que abre as leituras cruas. Sem ele, agrupar teria TIRADO
              informacao que a tela tinha.
        */
        /*
                A origem e uma celula como as outras desde que as colunas se arrastam
                (05/10/2026), e por isso sai da lista na posicao da coluna dela. Ela so
                existe na lista de DIAS: desenhada fixa na segunda posicao tambem nas
                leituras de um dia, empurrava toda medicao uma coluna para a direita
                (item 4.3 da ata de 15/09/2026, visto de novo em 30/09).
            */
        cell.source ? (
            <div class={Css.source}>
                <Show when={cell.sourceIcon}>
                    <Icon name={cell.sourceIcon} size={18} class={Css.sourceIcon} />
                </Show>
                <span>{cell.value}</span>
            </div>
        ) : (
            <Show when={cell.value !== '-'} fallback={<span class={Css.empty}>{cell.value}</span>}>
                <button
                    type="button"
                    onClick={() => void cell.onSelect()}
                    title={`Ver as medições que formaram ${cell.value}`}
                    class={Css.cell}
                >
                    <Show when={cell.statusName}>
                        <span
                            class={Css.dot}
                            style={{ 'background-color': cell.statusColor }}
                            title={cell.statusName}
                        />
                    </Show>
                    <span class={Css.stack}>
                        <span class={Css.row}>
                            <span class={Css.value}>{cell.value}</span>
                            <Show when={cell.single}>
                                <span class={Css.single} title="Uma leitura só nesta hora - o número não é uma média">
                                    ×1
                                </span>
                            </Show>
                        </span>
                        <Show when={cell.range}>{(range) => <span class={Css.range}>{range()}</span>}</Show>
                    </span>
                </button>
            </Show>
        )
    )
}
